import { Matrix4, PerspectiveCamera, Scene, Vector2, Vector3, Vector4, type Camera, type WebGLRenderer, type WebGLRenderTarget } from "three";
import type { FrameSnapshot, FrameStats } from "../../engine/frame-stats.ts";
import { createPipeline, type PipelineFactory, type PostPipeline } from "./post-effects.ts";
import { countPrograms, desaturated, GRADE_IDENTITY, GRADE_STYLE, halfFloatTargets, motionAllowed, MotionBlur, MOTION_FLOATS, postEffects, postParams, postPlan, POST_STYLE, samePlan, SHOCK, shockAllowed, SHOCK_FLOATS, Shockwaves, type PostCaps, type PostPlan, type PostSettings, type PostTier, type ShockCamera } from "./post-model.ts";
import { QUALITY } from "../quality.ts";

/** Os uniforms da atmosfera que o pós liga só enquanto desenha (F51/F52). */
export interface PostUniforms {
  /** A máscara do contorno no alfa (F51): o pós não a liga desde que o efeito de contorno saiu (Fase 15); a sonda, sim. */
  uOutlineMask: { value: number };
  uGlowGain: { value: number };
  /** Pixels do alvo por pixel CSS (o tamanho das partículas). */
  uPixelRatio?: { value: number };
  /** O sol na lente (Fase 13): direção, cor e a noite. */
  uSunDir?: { value: Vector3 };
  uLightColor?: { value: { r: number; g: number; b: number } };
  uNight?: { value: number };
}

/**
 * Compila os programas da cena para a saída de cor do alvo dado (`null`: o canvas, sRGB; um alvo:
 * linear). O three escolhe o programa pelo alvo atual na hora da chamada (`compileAsync`).
 */
export type Precompile = (scene: Scene, camera: Camera, target: WebGLRenderTarget | null) => Promise<unknown>;

/** A compilação padrão: só a cena, com o alvo ligado durante a chamada. */
export const compileScene =
  (renderer: WebGLRenderer): Precompile =>
  (scene, camera, target) => {
    const previous = renderer.getRenderTarget();
    renderer.setRenderTarget(target);
    try {
      return renderer.compileAsync(scene, camera);
    } finally {
      renderer.setRenderTarget(previous);
    }
  };

export interface PostFxOptions {
  /** Monta o compositor (os testes injetam um falso). */
  factory?: PipelineFactory;
  /** O que vale no quadro; padrão: `QUALITY.post`. */
  settings?: () => PostSettings;
  /** Sem ele, lido do renderer na primeira vez (`halfFloatTargets`). */
  caps?: PostCaps;
  /** Tamanho fixo e a imagem final num alvo (a sonda de sombreamento), não no canvas. */
  size?: readonly [number, number];
  /** O relógio das ondas de choque (ms; padrão: `performance.now`). A vitrine passa o dela, em passos fixos. */
  clock?: () => number;
}

export interface PostStats {
  /** O tier pedido (as Opções; no Automático, o medido). */
  tier: PostTier;
  /** Há compositor desenhando agora. */
  composer: boolean;
  /**
   * Efeitos do compositor que existe (não do plano pedido: numa troca em curso, os de antes), na
   * ordem de `postEffects` (a `cor` sempre, com o compositor).
   */
  effects: string[];
  /** Troca entre o canvas direto e o compositor esperando a compilação dos programas da cena. */
  switching: boolean;
  /** Correção de cor ligada (sem ela, o efeito de cor passa a cor igual). */
  grade: boolean;
  float: boolean;
  multisampling: number;
  bloom: number;
  vignette: number;
  /** Draw calls do pós no último quadro e programas compilados do pós. */
  calls: number;
  programs: number;
  /** Ondas de choque vivas (F94; as marcadas para depois contam). */
  waves: number;
}

type Path = "direto" | "compositor";

/** Uma troca de caminho não espera mais que isto pela compilação (contexto perdido, promessa presa). */
const SWITCH_TIMEOUT_MS = 10_000;

/**
 * Pós-processamento (F52): decide a cada quadro, por `QUALITY.post` e pelo que o navegador oferece,
 * se a cena vai direto ao canvas (Baixo, ou tudo desligado: nenhum compositor existe) ou pelo
 * compositor, criado na primeira vez que um plano pede e jogado fora quando o plano muda ou volta
 * ao Baixo. O aperto de mão com a atmosfera: enquanto o compositor desenha, os emissivos sobem acima
 * de 1 onde há bloom, Médio e Alto (`uGlowGain`, o limiar do bloom) e o `autoClear` fica desligado (o
 * pacote limpa pelo próprio passe); no `finally`, tudo volta (o Baixo, as sondas e os ícones dependem
 * do `autoClear`). A máscara do contorno no alfa (F51) fica como está: desligada no jogo, onde o WebKit
 * mostraria o alfa.
 *
 * Trocar entre o canvas direto e o compositor no meio do jogo (as Opções, ou o Automático escolhendo
 * o Baixo depois de medir no Médio) muda a saída de cor de todos os programas da cena (sRGB no
 * canvas, linear num alvo): compilados no próximo quadro, travariam o jogo. A troca espera: os
 * programas compilam para a saída nova (`compileAsync`, em paralelo onde há
 * `KHR_parallel_shader_compile`; a cena de jogo põe a pré-compilação inteira, com as variantes) e o
 * caminho de antes continua desenhando até eles ficarem prontos.
 */
export class PostFx {
  /** Apaga a vinheta (0 a 1) enquanto uma vinheta CSS do jogo está na tela (`postParams`). */
  vignetteFade = 0;
  /** Dessaturação da cena (0 a 1, F75): vida baixa e afogando. Sem compositor (Baixo), fica só o halo CSS. */
  desaturate = 0;
  private readonly renderer: WebGLRenderer;
  private readonly factory: PipelineFactory;
  private readonly settings: () => PostSettings;
  private readonly size: readonly [number, number] | undefined;
  private readonly defaultPrecompile: Precompile;
  private precompile: Precompile;
  private caps: PostCaps | undefined;
  private uniforms: PostUniforms | undefined;
  private pipeline: PostPipeline | undefined;
  private active: PostPlan | undefined;
  /** O caminho do último quadro desenhado. */
  private path: Path | undefined;
  /** Troca de caminho em curso (com o compositor novo, se a troca é para ele). */
  private switching: { to: Path; ready: boolean; pipeline: PostPipeline | undefined; plan: PostPlan } | undefined;
  private generation = 0;
  private readonly clock: () => number;
  /** As ondas de choque (F94) e o quadro delas, sem alocar. */
  private readonly waves = new Shockwaves();
  private readonly waveData = new Float32Array(SHOCK_FLOATS);
  private readonly viewProj = new Matrix4();
  private readonly waveCamera: ShockCamera = { viewProj: this.viewProj.elements, focal: 1, x: 0, y: 0, z: 0 };
  /** A câmera do último quadro desenhado (a onda longe dela nem nasce). */
  private camera: Camera | undefined;
  /** O desfoque de movimento (Fase 14): o pedido no mundo, projetado no desenho pela câmera do quadro. */
  private readonly motionBlur = new MotionBlur();
  private readonly motionData = new Float32Array(MOTION_FLOATS);
  private streakAsk: { x: number; y: number; z: number; vx: number; vy: number; vz: number; strength: number } | undefined;
  private readonly motionA = new Vector3();
  private readonly motionB = new Vector3();

  constructor(renderer: WebGLRenderer, options: PostFxOptions = {}) {
    this.renderer = renderer;
    this.factory = options.factory ?? createPipeline;
    this.settings = options.settings ?? (() => QUALITY.post);
    this.caps = options.caps;
    this.size = options.size;
    this.clock = options.clock ?? (() => performance.now());
    this.defaultPrecompile = compileScene(renderer);
    this.precompile = this.defaultPrecompile;
  }

  /**
   * Os uniforms da atmosfera da cena desenhada (cada partida e cada vitrine tem a sua) e, opcional, a
   * compilação para uma troca de caminho (a cena de jogo passa a pré-compilação dela).
   */
  bind(uniforms: PostUniforms | undefined, precompile?: Precompile): void {
    this.uniforms = uniforms;
    this.precompile = precompile ?? this.defaultPrecompile;
  }

  /** O plano deste quadro. */
  plan(): PostPlan {
    const s = this.settings();
    this.caps ??= { float: halfFloatTargets(this.renderer.extensions) };
    return postPlan(s.tier, s, this.caps);
  }

  /** Há compositor agora (criado e com o plano em uso). */
  get live(): boolean {
    return this.pipeline !== undefined;
  }

  /** O alvo em que a cena é desenhada pelo compositor (a pré-compilação liga o mesmo formato de saída). */
  sceneTarget(): WebGLRenderTarget | null {
    return this.pipeline?.sceneTarget ?? null;
  }

  /** Com tamanho fixo: o alvo com a imagem final. */
  get output(): WebGLRenderTarget | undefined {
    return this.size ? this.pipeline?.output : undefined;
  }

  /**
   * Onda de choque (F94) num impacto em (x, y, z), a partir de `now` (ms do relógio do `PostFx`; pode ser
   * no futuro), com a força (0 a 1) e o raio final (m). Nada fora do compositor do Alto, com "Reduzir
   * lampejos" ou longe da câmera (`SHOCK.far`). Devolve se nasceu.
   */
  shockwave(x: number, y: number, z: number, strength: number, now: number, radius: number = SHOCK.radius): boolean {
    if (!this.pipeline || !shockAllowed(this.active, this.settings())) return false;
    const eye = this.camera?.matrixWorld.elements;
    if (eye && Math.hypot(x - eye[12]!, y - eye[13]!, z - eye[14]!) >= SHOCK.far) return false;
    return this.waves.spawn(x, y, z, strength, radius, now);
  }

  /**
   * Desfoque de movimento (Fase 14): neste quadro, o corpo em (x, y, z) anda (vx, vy, vz) m/s (a Investida, o
   * Salto); força 0 a 1. Vale só no quadro pedido. Nada sem o efeito no plano ou com "Reduzir lampejos".
   */
  motionStreak(x: number, y: number, z: number, vx: number, vy: number, vz: number, strength: number): void {
    if (!motionAllowed(this.active, this.settings())) return;
    this.streakAsk = { x, y, z, vx, vy, vz, strength };
  }

  /** O zoom do golpe forte (Fase 14) a partir do impacto em (x, y, z), força 0 a 1. Devolve se valeu. */
  motionImpact(x: number, y: number, z: number, strength: number): boolean {
    const camera = this.camera;
    if (!camera || !motionAllowed(this.active, this.settings())) return false;
    const p = this.motionA.set(x, y, z).project(camera);
    if (p.z >= 1 || Math.abs(p.x) > 1.2 || Math.abs(p.y) > 1.2) return false;
    this.motionBlur.impact(p.x * 0.5 + 0.5, p.y * 0.5 + 0.5, strength, this.clock());
    return true;
  }

  /** Desenha a cena: direto ao canvas (ou ao alvo atual) sem compositor; senão, pelo compositor. */
  render(scene: Scene, camera: Camera, dt: number, stats?: FrameStats): void {
    this.camera = camera;
    const pixels = this.uniforms?.uPixelRatio;
    if (pixels) pixels.value = this.size ? 1 : this.renderer.getPixelRatio();
    const plan = this.plan();
    const to: Path = plan.composer ? "compositor" : "direto";
    const run = (pipeline: PostPipeline) => (stats ? stats.measurePost(() => pipeline.render(dt)) : pipeline.render(dt));
    if (this.path !== undefined && this.path !== to && !this.size) {
      if (this.switching?.to !== to) this.beginSwitch(plan, to, scene, camera);
      const switching = this.switching!;
      if (!switching.ready) {
        // Os programas ainda compilam para a saída nova: o caminho de antes desenha.
        if (this.path === "direto") this.renderer.render(scene, camera);
        else this.draw(this.active!, scene, camera, run);
        return;
      }
      this.switching = undefined;
      if (switching.pipeline) {
        this.release();
        this.pipeline = switching.pipeline;
        this.active = switching.plan;
      }
    } else if (this.switching) this.dropSwitch();
    if (!plan.composer) {
      this.release();
      this.renderer.render(scene, camera);
      this.path = "direto";
      return;
    }
    this.draw(plan, scene, camera, run);
    this.path = "compositor";
  }

  /**
   * Aquecimento (no carregamento): cria o compositor do plano atual e desenha uma vez uma cena vazia
   * com o canvas cortado a nada (`scissor`), compilando os programas do pós sem mudar a tela. Devolve
   * se havia o que aquecer.
   */
  warm(): boolean {
    const plan = this.plan();
    if (!plan.composer) {
      this.release();
      return false;
    }
    if (this.pipeline && this.active && samePlan(this.active, plan)) return true;
    const renderer = this.renderer;
    const scissor = renderer.getScissor(new Vector4());
    const test = renderer.getScissorTest();
    renderer.setScissor(0, 0, 0, 0);
    renderer.setScissorTest(true);
    try {
      this.draw(plan, new Scene(), new PerspectiveCamera(), (pipeline) => pipeline.render(0));
    } finally {
      renderer.setScissor(scissor);
      renderer.setScissorTest(test);
    }
    return true;
  }

  stats(frame?: FrameSnapshot): PostStats {
    const plan = this.plan();
    const live = this.pipeline ? this.active : undefined;
    const params = postParams(this.settings(), this.vignetteFade);
    return {
      tier: plan.tier,
      composer: live !== undefined,
      effects: live ? postEffects(live) : [],
      switching: this.switching !== undefined,
      grade: live?.grade ?? false,
      float: plan.float,
      multisampling: this.pipeline?.multisampling ?? 0,
      bloom: live?.bloom ? params.bloom : 0,
      vignette: live?.vignette ? params.vignette : 0,
      calls: frame?.postCalls ?? 0,
      programs: countPrograms(this.renderer.info.programs).post,
      waves: live?.onda ? this.waves.alive(this.clock()) : 0,
    };
  }

  /** Joga fora o compositor (e os alvos e programas dele) e uma troca em curso. Idempotente. */
  dispose(): void {
    this.dropSwitch();
    this.release();
    this.path = undefined;
  }

  private release(): void {
    this.waves.clear();
    this.motionBlur.clear();
    if (!this.pipeline) return;
    this.pipeline.dispose();
    this.pipeline = undefined;
    this.active = undefined;
  }

  /** Começa a troca: o compositor novo (se for para ele) e a compilação da cena para a saída dele. */
  private beginSwitch(plan: PostPlan, to: Path, scene: Scene, camera: Camera): void {
    this.dropSwitch();
    const generation = this.generation;
    const pipeline = to === "compositor" ? this.create(plan, scene, camera) : undefined;
    const switching = { to, ready: false, pipeline, plan };
    this.switching = switching;
    const done = () => {
      clearTimeout(timer);
      // Uma troca abandonada (o jogador voltou atrás, ou a cena saiu) não vale mais.
      if (this.generation === generation) switching.ready = true;
    };
    const timer = setTimeout(done, SWITCH_TIMEOUT_MS);
    let pending: Promise<unknown>;
    try {
      pending = this.precompile(scene, camera, pipeline?.sceneTarget ?? null);
    } catch (error) {
      pending = Promise.reject(error);
    }
    pending.then(done, (error: unknown) => {
      console.warn("pré-compilação da troca do pós", error);
      done();
    });
  }

  private dropSwitch(): void {
    this.generation++;
    this.switching?.pipeline?.dispose();
    this.switching = undefined;
  }

  /** O compositor do plano; o pacote desliga o `autoClear` ao nascer, e ele volta aqui. */
  private create(plan: PostPlan, scene: Scene, camera: Camera): PostPipeline {
    const autoClear = this.renderer.autoClear;
    try {
      return this.factory(this.renderer, plan, scene, camera, this.size);
    } finally {
      this.renderer.autoClear = autoClear;
    }
  }

  /**
   * O quadro das ondas pela câmera do quadro: sem nenhuma viva, só zeros (sem projetar); com "Reduzir
   * lampejos", as vivas somem.
   */
  private waveFrame(camera: Camera): Float32Array {
    const now = this.clock();
    if (this.settings().reduceFlashes) this.waves.clear();
    const view = this.waveCamera;
    if (this.waves.alive(now) > 0) {
      // A vista deste quadro: o renderer só a atualiza dentro do desenho, depois daqui.
      camera.updateMatrixWorld();
      this.viewProj.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
      view.focal = camera.projectionMatrix.elements[5]!;
      const m = camera.matrixWorld.elements;
      view.x = m[12]!;
      view.y = m[13]!;
      view.z = m[14]!;
    }
    this.waves.write(now, view, this.waveData);
    return this.waveData;
  }

  /** O quadro do desfoque pela câmera do quadro (o rastro pedido projetado: a direção na tela, na altura dela). */
  private motionFrame(camera: Camera): Float32Array {
    const ask = this.streakAsk;
    this.streakAsk = undefined;
    if (this.settings().reduceFlashes) this.motionBlur.clear();
    else if (ask) {
      camera.updateMatrixWorld();
      const a = this.motionA.set(ask.x, ask.y, ask.z).project(camera);
      const b = this.motionB.set(ask.x + ask.vx * 0.1, ask.y + ask.vy * 0.1, ask.z + ask.vz * 0.1).project(camera);
      if (a.z < 1 && b.z < 1) {
        const aspect = camera instanceof PerspectiveCamera ? camera.aspect : 1;
        this.motionBlur.setStreak(a.x * 0.5 + 0.5, a.y * 0.5 + 0.5, (b.x - a.x) * 0.5 * aspect, (b.y - a.y) * 0.5, ask.strength, aspect);
      }
    }
    return this.motionBlur.write(this.clock(), this.motionData);
  }

  /** O sol na tela pela câmera do quadro (Fase 13): à frente dela e acima do horizonte, com a cor da luz. */
  private sunFrame(pipeline: PostPipeline, camera: Camera): void {
    const u = this.uniforms;
    if (!u?.uSunDir || !u.uLightColor) return;
    camera.updateMatrixWorld();
    const dir = u.uSunDir.value;
    const eye = this.sunPoint.setFromMatrixPosition(camera.matrixWorld);
    this.sunPoint.copy(eye).addScaledVector(dir, 1000).project(camera);
    const front = this.sunPoint.z < 1 && dir.y > -0.05 && (u.uNight?.value ?? 0) < 0.5;
    this.sunUv.set(this.sunPoint.x * 0.5 + 0.5, this.sunPoint.y * 0.5 + 0.5);
    const c = u.uLightColor.value;
    const fade = Math.max(0, 1 - (u.uNight?.value ?? 0) * 2);
    this.sunColor.set(c.r * fade, c.g * fade, c.b * fade);
    pipeline.setSun(this.sunUv, this.sunColor, front);
  }

  private readonly sunPoint = new Vector3();
  private readonly sunUv = new Vector2();
  private readonly sunColor = new Vector3();

  private draw(plan: PostPlan, scene: Scene, camera: Camera, run: (pipeline: PostPipeline) => void): void {
    const renderer = this.renderer;
    const autoClear = renderer.autoClear;
    const gain = this.uniforms?.uGlowGain;
    const gained = gain?.value ?? 1;
    try {
      if (!this.pipeline || !this.active || !samePlan(this.active, plan)) {
        this.release();
        this.pipeline = this.create(plan, scene, camera);
        this.active = plan;
      }
      const pipeline = this.pipeline;
      pipeline.setView(scene, camera);
      pipeline.fit();
      pipeline.setParams({ ...postParams(this.settings(), this.vignetteFade), grade: desaturated(plan.grade ? GRADE_STYLE : GRADE_IDENTITY, this.desaturate), reduceFlashes: this.settings().reduceFlashes });
      if (plan.rays || plan.flare) this.sunFrame(pipeline, camera);
      if (plan.onda) pipeline.setWaves(this.waveFrame(camera));
      if (plan.motion) pipeline.setMotion(this.motionFrame(camera));
      if (gain && plan.bloom) gain.value = POST_STYLE.glowLuminance;
      renderer.autoClear = false;
      run(pipeline);
    } finally {
      renderer.autoClear = autoClear;
      if (gain) gain.value = gained;
    }
  }
}
